import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { WithholdingCertificateType } from '@erp/data-access/interfaces';
import { buildCertificateFromInvoice, formatIdTypeOperation, isWithholdingApplicable } from '@erp/domain/tax';
import {
  useCertificateBySource,
  useCreateCertificate,
} from '@/hooks/use-withholding';
import { useDataService } from '@/lib/service-context';
import { useCompanySettings } from '@/hooks/use-settings';
import { Button } from '@erp/ui';
import { FileText } from '@/components/icons';

interface Props {
  certificateType: WithholdingCertificateType;
  sourceDocument: {
    id: string;
    number: string;
    docDate: string;
    totalHt: number;
    totalVat: number;
    totalTtc: number;
  };
  thirdParty: {
    id: string;
    type: 'company' | 'individual';
  };
}

/**
 * Bouton "Créer RAS" depuis une facture de vente ou d'achat.
 * Si un certificat existe déjà pour cette facture, redirige vers sa fiche.
 */
export function CreateCertificateButton({ certificateType, sourceDocument, thirdParty }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: existing, isLoading } = useCertificateBySource(sourceDocument.id);
  const { data: settings } = useCompanySettings();
  const createMut = useCreateCertificate();
  const ds = useDataService();

  const basePath = certificateType === 'sale' ? '/withholding/sales' : '/withholding/purchases';
  const withholdingThreshold = settings?.withholdingThreshold ?? 1000;
  const canCreate = isWithholdingApplicable(thirdParty.type, sourceDocument.totalTtc, withholdingThreshold);

  if (isLoading) return null;

  if (existing) {
    return (
      <Button variant="outline" disabled title={t('withholding.certificateAlreadyExists')}>
        <FileText size={14} className="mr-1.5" />
        {t('withholding.certificateAlreadyExists')}
      </Button>
    );
  }

  const proceedCreate = async (draft: ReturnType<typeof buildCertificateFromInvoice>) => {
    const seq = await ds.withholding.getNextOperationSequence();
    const lines = draft.lines.map((l) => ({
      ...l,
      idTypeOperation: formatIdTypeOperation(1, seq),
      operationTypeId: null,
    }));

    const certificate = await createMut.mutateAsync({
      certificateType,
      thirdPartyType: certificateType === 'sale' ? 'client' : 'supplier',
      thirdPartyId: thirdParty.id,
      sourceDocumentIds: [sourceDocument.id],
      datePayment: new Date().toISOString().slice(0, 10),
      refCertif: `${new Date().getFullYear()}/${sourceDocument.number}`,
      totalHt: draft.totalHt,
      totalVat: draft.totalVat,
      totalTtc: draft.totalTtc,
      totalRas: draft.totalRas,
      totalNetServi: draft.totalNetServi,
      lines,
    });

    navigate(`${basePath}/${certificate.id}`);
  };

  const handleCreate = async () => {
    if (!settings || !canCreate) return;

    const avgVatRate = sourceDocument.totalHt > 0
      ? Math.round((sourceDocument.totalVat / sourceDocument.totalHt) * 100)
      : 0;

    const draft = buildCertificateFromInvoice(
      {
        docDate: sourceDocument.docDate,
        totalHt: sourceDocument.totalHt,
        totalVat: sourceDocument.totalVat,
        totalTtc: sourceDocument.totalTtc,
        vatRate: avgVatRate,
      },
      { type: thirdParty.type },
      {
        withholdingRate: settings.withholdingRate ?? 1,
        withholdingThreshold: settings.withholdingThreshold ?? 1000,
      },
      1,
      1,
    );

    await proceedCreate(draft);
  };

  return (
    <Button
      variant="outline"
      onClick={handleCreate}
      disabled={createMut.isPending || !settings || !canCreate}
      title={!canCreate ? t('withholding.notApplicable') : undefined}
    >
      <FileText size={14} className="mr-1.5" />
      {t('withholding.createCertificate')}
    </Button>
  );
}
